import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router';
import { ArrowLeft, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { useBot, useBotTrades, useBotCommands, useBotTransactions } from '@/hooks/use-bots';
import { Spinner } from '@/components/ui/spinner';
import { formatCurrency, formatPnl, botCurrency } from '@/lib/format';

const ENTRIES_PER_PAGE = 50;

interface LogEntry {
  id: string;
  time: string;
  category: 'trade' | 'command' | 'capital';
  level: 'INFO' | 'WARN' | 'ERROR';
  label: string;
  detail: string;
  color: string;
}

type FilterCategory = 'all' | 'trade' | 'command' | 'capital';

export default function BotActivityLogPage() {
  const { id } = useParams<{ id: string }>();
  const { data: bot, isLoading: botLoading } = useBot(id!);
  const { data: tradeResult } = useBotTrades(id!, { limit: 100 });
  const { data: cmdResult } = useBotCommands(id!, { limit: 100 });
  const { data: txResult } = useBotTransactions(id!, { limit: 100 });

  const [filter, setFilter] = useState<FilterCategory>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const currency = bot ? botCurrency(bot) : 'USD';

  // Normalize all data into LogEntry[]
  const allEntries = useMemo(() => {
    const items: LogEntry[] = [];

    // Bot created event
    if (bot) {
      items.push({
        id: 'bot-created',
        time: bot.created_at,
        category: 'command',
        level: 'INFO',
        label: 'Bot Created',
        detail: `name="${bot.name}" symbol=${bot.symbol}`,
        color: 'text-ink-4',
      });
    }

    // Commands
    const cmdMap: Record<string, { label: string; level: 'INFO' | 'WARN' | 'ERROR'; color: string }> = {
      START: { label: 'Bot Started', level: 'INFO', color: 'text-green' },
      STOP: { label: 'Bot Stopped', level: 'WARN', color: 'text-red' },
      RESTART: { label: 'Bot Restarted', level: 'INFO', color: 'text-amber' },
      DEPOSIT: { label: 'Capital Deposited', level: 'INFO', color: 'text-green' },
      WITHDRAW: { label: 'Capital Withdrawn', level: 'WARN', color: 'text-red' },
      DRAIN: { label: 'Drain Activated', level: 'WARN', color: 'text-amber' },
    };
    for (const cmd of cmdResult?.commands ?? []) {
      const mapped = cmdMap[cmd.command] ?? { label: cmd.command.toLowerCase(), level: 'INFO' as const, color: 'text-ink-3' };
      if (cmd.status === 'FAILED') {
        items.push({
          id: `cmd-${cmd.id}`,
          time: cmd.processed_at ?? cmd.created_at,
          category: 'command',
          level: 'ERROR',
          label: `${mapped.label} — Failed`,
          detail: cmd.error ?? 'Unknown error',
          color: 'text-red',
        });
      } else if (cmd.status === 'DONE') {
        items.push({
          id: `cmd-${cmd.id}`,
          time: cmd.processed_at ?? cmd.created_at,
          category: 'command',
          ...mapped,
          detail: '',
        });
      } else {
        items.push({
          id: `cmd-${cmd.id}`,
          time: cmd.created_at,
          category: 'command',
          level: 'INFO',
          label: `${mapped.label} — ${cmd.status === 'PENDING' ? 'Pending' : 'Processing'}`,
          detail: '',
          color: 'text-amber',
        });
      }
    }

    // Transactions
    for (const tx of txResult?.transactions ?? []) {
      items.push({
        id: `tx-${tx.id}`,
        time: tx.created_at,
        category: 'capital',
        level: 'INFO',
        label: tx.type === 'DEPOSIT' ? 'Deposited Capital' : 'Withdrew Capital',
        detail: `amount=${formatCurrency(tx.amount, currency)} balance=${formatCurrency(tx.balance_after, currency)}${tx.note ? ` note="${tx.note}"` : ''}`,
        color: tx.type === 'DEPOSIT' ? 'text-green' : 'text-red',
      });
    }

    // Trades
    for (const trade of tradeResult?.trades ?? []) {
      // Open event
      items.push({
        id: `trade-open-${trade.id}`,
        time: trade.opened_at,
        category: 'trade',
        level: 'INFO',
        label: `Opened ${trade.type} Position`,
        detail: `vol=${trade.volume} price=${trade.open_price.toFixed(2)}${trade.trigger_reason_text ? ` reason="${trade.trigger_reason_text}"` : ''}`,
        color: trade.type === 'BUY' ? 'text-green' : 'text-red',
      });
      // Close event
      if (trade.status === 'CLOSED' && trade.closed_at) {
        const pnl = trade.net_pnl ?? trade.gross_pnl;
        items.push({
          id: `trade-close-${trade.id}`,
          time: trade.closed_at,
          category: 'trade',
          level: pnl < 0 ? 'WARN' : 'INFO',
          label: `Closed ${trade.type} Position`,
          detail: `pnl=${formatPnl(pnl, currency)}${trade.exit_reason_text ? ` reason="${trade.exit_reason_text}"` : ''}`,
          color: pnl >= 0 ? 'text-green' : 'text-red',
        });
      }
    }

    items.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
    return items;
  }, [bot, tradeResult, cmdResult, txResult, currency]);

  // Category counts
  const counts = useMemo(() => {
    const c = { all: allEntries.length, trade: 0, command: 0, capital: 0 };
    for (const e of allEntries) c[e.category]++;
    return c;
  }, [allEntries]);

  // Filter → search → paginate
  const filtered = useMemo(() => {
    let result = allEntries;
    if (filter !== 'all') result = result.filter((e) => e.category === filter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((e) => e.label.toLowerCase().includes(q) || e.detail.toLowerCase().includes(q));
    }
    return result;
  }, [allEntries, filter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ENTRIES_PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const paginated = filtered.slice((safePage - 1) * ENTRIES_PER_PAGE, safePage * ENTRIES_PER_PAGE);

  const fmtTs = (d: string) => {
    const dt = new Date(d);
    const date = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const time = dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    return `${date} ${time}`;
  };

  const levelColor = (level: string) =>
    level === 'WARN' ? 'text-amber' : level === 'ERROR' ? 'text-red' : 'text-ink-4';

  const handleFilterChange = (f: FilterCategory) => {
    setFilter(f);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  if (botLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!bot) {
    return (
      <div className="glass-sm rounded-lg p-6 text-center">
        <p className="text-red text-sm font-semibold">Bot not found</p>
      </div>
    );
  }

  const filters: { key: FilterCategory; label: string }[] = [
    { key: 'all', label: `All (${counts.all})` },
    { key: 'trade', label: `Trades (${counts.trade})` },
    { key: 'command', label: `Commands (${counts.command})` },
    { key: 'capital', label: `Capital (${counts.capital})` },
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <Link to={`/bots/${id}`} className="inline-flex items-center gap-1 text-xs text-ink-3 hover:text-ink-1 mb-3">
          <ArrowLeft size={14} />
          Back to {bot.name}
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-extrabold tracking-tight">Activity Log</h1>
            <p className="text-xs text-ink-3 mt-0.5">
              {filtered.length} {filtered.length === 1 ? 'entry' : 'entries'}
              {filter !== 'all' && ` (filtered)`}
            </p>
          </div>
        </div>
      </div>

      {/* Filters + Search */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex gap-1">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => handleFilterChange(f.key)}
              className={`text-[10px] font-bold px-2.5 py-1 rounded-md transition-all cursor-pointer ${
                filter === f.key
                  ? 'bg-orange-bg text-orange border border-orange/20'
                  : 'bg-surface text-ink-3 border border-glass-border hover:bg-surface-2'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-4" />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search entries..."
            className="w-full pl-7 pr-3 py-1.5 text-[11px] font-mono bg-surface border border-glass-border rounded-md text-ink-1 placeholder:text-ink-4 focus:outline-none focus:border-orange/40"
          />
        </div>
      </div>

      {/* Terminal body */}
      <div className="glass-sm rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-glass-border flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green animate-pulse" />
          <p className="text-[10px] font-mono font-semibold text-ink-4">log — {bot.name}</p>
        </div>
        <div className="bg-glass px-3 py-2 font-mono text-[11px] leading-relaxed">
          {paginated.length === 0 ? (
            <p className="text-ink-4 py-4 text-center">
              {search ? 'No entries match your search.' : 'No activity yet.'}
            </p>
          ) : (
            paginated.map((entry) => (
              <div key={entry.id} className="flex gap-0 flex-wrap py-px">
                <span className="text-ink-4 shrink-0">{fmtTs(entry.time)}</span>
                <span className={`mx-1.5 shrink-0 ${levelColor(entry.level)}`}>{entry.level.padEnd(5)}</span>
                <span className={entry.color}>{entry.label}</span>
                {entry.detail && <span className="text-ink-4 ml-1.5">{entry.detail}</span>}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-[10px] text-ink-4">
            Page {safePage} of {totalPages} ({filtered.length} total)
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={safePage <= 1}
              className="px-2 py-1 rounded-md bg-surface border border-glass-border text-[10px] font-bold text-ink-3 hover:bg-surface-2 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              First
            </button>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="p-1.5 rounded-md bg-surface border border-glass-border text-ink-3 hover:bg-surface-2 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              <ChevronLeft size={14} />
            </button>
            {getPageNumbers(safePage, totalPages).map((p, i) =>
              p === '...' ? (
                <span key={`ellipsis-${i}`} className="px-1 text-[10px] text-ink-4">...</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setPage(p as number)}
                  className={`min-w-[28px] py-1 rounded-md text-[10px] font-bold cursor-pointer transition-all ${
                    safePage === p
                      ? 'bg-orange-bg text-orange border border-orange/20'
                      : 'bg-surface border border-glass-border text-ink-3 hover:bg-surface-2'
                  }`}
                >
                  {p}
                </button>
              ),
            )}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="p-1.5 rounded-md bg-surface border border-glass-border text-ink-3 hover:bg-surface-2 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              <ChevronRight size={14} />
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={safePage >= totalPages}
              className="px-2 py-1 rounded-md bg-surface border border-glass-border text-[10px] font-bold text-ink-3 hover:bg-surface-2 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function getPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | '...')[] = [];
  const around = new Set<number>();

  around.add(1);
  around.add(total);
  for (let i = current - 1; i <= current + 1; i++) {
    if (i >= 1 && i <= total) around.add(i);
  }

  const sorted = Array.from(around).sort((a, b) => a - b);

  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i]! - sorted[i - 1]! > 1) {
      pages.push('...');
    }
    pages.push(sorted[i]!);
  }

  return pages;
}
